import React, { useEffect, useState, useMemo } from "react";
import { Tooltip } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretRight } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector } from "react-redux";
// CUSTOM HOOKS
import { useLastLiftMessage } from "../socketMessageState/BankMessagesContext";
// COMPONENTS
import IconDefinition from "../iconDefinition/IconDefinition";
// INTERFACES
import { RootState } from "../../../app/store";
import IconDefinitionEscalatorDefault from "../iconDefinition/IconDefinitionEscalatorDefault";
import MonitoringIconsMovingWalks from "../monitoringIcons/MonitoringIconsMovingWalks";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
// UTILS
import { emptyFunctionDeclaration } from "../../../utils/utils";
import { getServiceModeDisplay } from "../../../utils/serviceModeDisplay";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
import { BLANK_LIFT } from "../../../constants/lifts";
import { stringToUuidT } from "../../../utils/uuid";

function MonitoringMapViewPin3({
  carName,
  icon,
  direction,
  orientation,
  liftId,
  liftType,
}: {
  carName: string;
  icon: string;
  direction: "top" | "left" | "bottom" | "right";
  orientation: "horizontal" | "vertical";
  liftId: string;
  liftType: string;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [showContent, setShowContent] = useState<boolean>(false);
  const [boxSize, setBoxSize] = useState<"sm" | "md" | "lg">("sm");
  const [canShowServiceMode, setCanShowServiceMode] = useState<boolean>(false);
  const [canShowName, setCanShowName] = useState<boolean>(false);
  const [canShowFloors, setCanShowFloors] = useState<boolean>(false);

  // ************************************************ */
  // USE STATE VARIABLES **************************** */

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);
  const { serviceModeEnabled, floorsEnabled, namesEnabled } = useSelector(
    (state: any) => state.mapView,
  );

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const liftUuid = useMemo(() => stringToUuidT(liftId), [liftId]);
  const socketMessageLift: SocketMessageInterface =
    useLastLiftMessage(liftUuid) || BLANK_SOCKET_MESSAGE;
  const hasFaults: boolean = socketMessageLift.faults;

  const serviceModeInfo = useMemo(
    () =>
      getServiceModeDisplay(
        socketMessageLift.serviceMode?.messageType ?? null,
        socketMessageLift.serviceMode?.message?.message?.message ?? null,
      ),
    [socketMessageLift.serviceMode],
  );

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const handleMouseOver = () => {
    setShowContent(true);
  };
  const handleMouseLeave = () => {
    setShowContent(false);
  };
  const getPinClass = () => {
    let pinClass = "mapViewStyles__box";
    pinClass = pinClass.concat(" mapViewStyles__box__", theme);
    if (showContent || canShowServiceMode || canShowFloors || canShowName) {
      pinClass = pinClass.concat(
        " mapViewStyles__box__",
        boxSize,
        "__",
        orientation,
        "__",
        "expanded",
      );
    } else {
      pinClass = pinClass.concat(" mapViewStyles__box__minimized");
    }
    pinClass = pinClass.concat(" mapViewStyles__arrow__", direction, "__", theme);
    pinClass = pinClass.concat(" mapViewStyles__box__", boxSize, "__", orientation);
    if (hasFaults) {
      pinClass = pinClass.concat(" mapViewStyles__box__error__", direction, "__", theme);
    }
    return pinClass;
  };

  const getAlignItemClass = () => {
    if (direction === "top") {
      return "flex-start";
    }
    if (direction === "bottom") {
      return "flex-end";
    }
    return "center";
  };

  const getJustifyContentClass = () => {
    if (direction === "top") {
      return "center";
    }
    if (direction === "bottom") {
      return "center";
    }
    if (direction === "right") {
      return "flex-end";
    }
    if (direction === "left") {
      return "flex-start";
    }
    return "center";
  };

  const getAlignItems = () => {
    if (direction === "top") {
      return "flex-start";
    }
    if (direction === "bottom") {
      return "flex-end";
    }
    return "center";
  };

  const getJustifyItems = () => {
    if (direction === "left") {
      return "flex-start";
    }
    if (direction === "right") {
      return "flex-end";
    }
    return "center";
  };

  const adjustSize = () => {
    if (canShowServiceMode && canShowName && canShowFloors) {
      setBoxSize("lg");
    } else if (
      (canShowServiceMode && canShowName) ||
      (canShowServiceMode && canShowFloors) ||
      (canShowName && canShowFloors)
    ) {
      setBoxSize("md");
    } else {
      setBoxSize("sm");
    }
  };
  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if ((serviceModeEnabled || showContent) && socketMessageLift.serviceMode !== null)
      setCanShowServiceMode(true);
    else setCanShowServiceMode(false);

    if ((namesEnabled || showContent) && carName && carName !== "") setCanShowName(true);
    else setCanShowName(false);

    if (floorsEnabled || showContent) setCanShowFloors(true);
    else setCanShowFloors(false);

    adjustSize();
  }, [serviceModeEnabled, showContent, namesEnabled, carName, floorsEnabled]);

  useEffect(() => {
    adjustSize();
  }, [canShowServiceMode, canShowName, canShowFloors]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div
      style={{
        width: orientation === "vertical" ? 40 : 72,
        height: orientation === "vertical" ? 72 : 40,
        display: "flex",
        alignItems: orientation === "vertical" ? getAlignItems() : "",
        justifyContent: orientation !== "vertical" ? getJustifyItems() : "",
      }}
    >
      {/** ************************************************************** */}
      {/** VERTICAL */}
      {orientation === "vertical" && (
        <div
          className={`mapViewStyles__box__${boxSize}__vertical__expanded`}
          style={{
            justifyContent: getJustifyContentClass(),
            alignItems: getAlignItemClass(),
            display: "flex",
            borderRadius: "8px",
          }}
        >
          <div
            className={getPinClass()}
            onMouseOver={handleMouseOver}
            onMouseLeave={handleMouseLeave}
            onFocus={emptyFunctionDeclaration}
          >
            {/** CAR NAME */}
            {canShowName && (
              <div
                style={{
                  height: "16px",
                  fontSize: "10px",
                  width: "90%",
                  marginLeft: "5%",
                  marginRight: "5%",
                  paddingTop: "3px",
                }}
                className="generalStyles__textEllipsis"
              >
                {carName.length >= 8 ? (
                  <Tooltip placement="top" title={carName}>
                    <p>{carName}</p>
                  </Tooltip>
                ) : (
                  <p>{carName}</p>
                )}
              </div>
            )}
            {/** ICON */}
            {icon && (
              <div
                style={{
                  width: "100%",
                }}
              >
                {["elevator", "map"].includes(liftType.toLowerCase()) && (
                  <IconDefinition
                    isMap
                    expanded={showContent}
                    liftType={liftType?.toUpperCase()}
                    icon={icon}
                    iconSize={26}
                  />
                )}
                {liftType.toLowerCase() === "escalator" && (
                  <IconDefinitionEscalatorDefault
                    iconSize={28}
                    mapViewClass={
                      showContent || namesEnabled
                        ? "mapViewStyles__icon__escalator__expanded"
                        : "mapViewStyles__icon__escalator__minimized"
                    }
                    lift={BLANK_LIFT}
                  />
                )}
                {liftType.toLowerCase() === "movingwalk" && (
                  <MonitoringIconsMovingWalks
                    direction={socketMessageLift.direction}
                    iconSize={28}
                    mapViewClass={
                      showContent || namesEnabled
                        ? "mapViewStyles__icon__escalator__expanded"
                        : "mapViewStyles__icon__escalator__minimized"
                    }
                  />
                )}
              </div>
            )}
            {/** NEXT STOP */}
            {canShowFloors && (
              <Tooltip placement="bottom" title="First stop > Second Stop">
                <div
                  className="generalStyles__flex"
                  style={{
                    fontSize: "10px",
                    width: "100%",
                    height: "16px",
                  }}
                >
                  <div className="" style={{ width: "33%", textAlign: "center" }}>
                    <p>First stop</p>{" "}
                  </div>
                  <div className="" style={{ width: "33%", textAlign: "center" }}>
                    <FontAwesomeIcon icon={faCaretRight} />
                  </div>
                  <div className="" style={{ width: "33%", textAlign: "center" }}>
                    <p>Second Stop</p>
                  </div>
                </div>
              </Tooltip>
            )}
            {/** SERVICE MODE */}
            {canShowServiceMode && serviceModeInfo && (
              <Tooltip placement="bottom" title={serviceModeInfo.displayName}>
                <div
                  style={{
                    height: "16px",
                    width: "100%",
                    fontSize: "10px",
                    color: serviceModeInfo.color || "cyan",
                  }}
                >
                  {serviceModeInfo.abbreviation.substring(0, 3)}
                </div>
              </Tooltip>
            )}
          </div>
        </div>
      )}
      {/** ************************************************************** */}
      {/** HORIZONTAL */}
      {orientation === "horizontal" && (
        <div
          className={`mapViewStyles__box__${boxSize}__horizontal__expanded`}
          style={{
            justifyContent: getJustifyContentClass(),
            alignItems: getAlignItemClass(),
            display: "flex",
            borderRadius: "8px",
          }}
        >
          {" "}
          <div
            className={getPinClass()}
            onMouseOver={handleMouseOver}
            onMouseLeave={handleMouseLeave}
            onFocus={emptyFunctionDeclaration}
          >
            {/** CAR NAME */}
            {canShowName && (
              <div
                style={{
                  height: "16px",
                  fontSize: "10px",
                  width: "90%",
                  marginLeft: "5%",
                  marginRight: "5%",
                  paddingTop: "3px",
                }}
                className="generalStyles__textEllipsis"
              >
                {carName.length >= 8 ? (
                  <Tooltip placement="top" title={carName}>
                    <p>{carName}</p>
                  </Tooltip>
                ) : (
                  <p>{carName || "--"}</p>
                )}
              </div>
            )}
            <div className="generalStyles__flex">
              {/** NEXT STOP */}
              {canShowFloors && (
                <Tooltip placement="bottom" title="F > NST">
                  <div
                    className="generalStyles__flex"
                    style={{
                      fontSize: "10px",
                      width: "40px",
                      height: "32px",
                      paddingTop: "4px",
                    }}
                  >
                    <div className="" style={{ width: "33%", textAlign: "center" }}>
                      <p>FS</p>{" "}
                    </div>
                    <div className="" style={{ width: "33%", textAlign: "center" }}>
                      <FontAwesomeIcon icon={faCaretRight} />
                    </div>
                    <div className="" style={{ width: "33%", textAlign: "center" }}>
                      <p>SS</p>
                    </div>
                  </div>
                </Tooltip>
              )}
              {/** ICON */}
              {icon && (
                <div
                  style={{
                    width: "40px",
                  }}
                >
                  {["elevator", "map"].includes(liftType.toLowerCase()) && (
                    <IconDefinition
                      isMap
                      expanded={showContent}
                      liftType={liftType?.toUpperCase()}
                      icon={icon}
                      iconSize={36}
                    />
                  )}
                  {liftType.toLowerCase() === "escalator" && (
                    <IconDefinitionEscalatorDefault
                      iconSize={28}
                      lift={BLANK_LIFT}
                      mapViewClass={
                        showContent || namesEnabled
                          ? "mapViewStyles__icon__escalator__expanded"
                          : "mapViewStyles__icon__escalator__minimized"
                      }
                    />
                  )}
                  {liftType.toLowerCase() === "movingwalk" && (
                    <MonitoringIconsMovingWalks
                      direction={socketMessageLift.direction}
                      iconSize={28}
                      mapViewClass={
                        showContent || namesEnabled
                          ? "mapViewStyles__icon__escalator__expanded"
                          : "mapViewStyles__icon__escalator__minimized"
                      }
                    />
                  )}
                </div>
              )}
              {/** SERVICE MODE */}
              {canShowServiceMode && serviceModeInfo && (
                <Tooltip placement="bottom" title={serviceModeInfo.displayName}>
                  <div
                    style={{
                      height: "16px",
                      width: "100%",
                      fontSize: "10px",
                      color: serviceModeInfo.color || "cyan",
                    }}
                  >
                    {serviceModeInfo.abbreviation.substring(0, 3)}
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export default MonitoringMapViewPin3;
